// src/components/finance/PaymentMethodList.tsx
import React, { useState, useEffect } from "react";
import { StripePaymentMethod } from "../../types/finance/FinanceTypes";
import { listPaymentMethods, detachPaymentMethod, setDefaultPaymentMethod } from "../../api/finance/FinanceApi";
import { useNotifications } from "../../context/NotificationContext";
import PaymentMethodListView from "./PaymentMethodListView";

interface PaymentMethodListProps {
	customerId: string;
	onAddNew: () => void;
	refreshTrigger?: number;
}

const PaymentMethodList: React.FC<PaymentMethodListProps> = ({ customerId, onAddNew, refreshTrigger = 0 }) => {
	const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadPaymentMethods();
	}, [customerId, refreshTrigger]);

	const loadPaymentMethods = async () => {
		setLoading(true);
		try {
			const methods = await listPaymentMethods(customerId);
			setPaymentMethods(methods);
		} catch (error) {
			console.error("Error loading payment methods:", error);
			showError("Failed to load payment methods");
		} finally {
			setLoading(false);
		}
	};

	const handleSetDefault = async (paymentMethodId: string) => {
		setActionLoading(paymentMethodId);
		try {
			await setDefaultPaymentMethod({
				paymentMethodId,
				customerId,
			});
			showSuccess("Default payment method updated");
			await loadPaymentMethods();
		} catch (error) {
			console.error("Error setting default payment method:", error);
			showError("Failed to update default payment method");
		} finally {
			setActionLoading(null);
		}
	};

	const handleDelete = async (paymentMethodId: string) => {
		setActionLoading(paymentMethodId);
		try {
			await detachPaymentMethod(paymentMethodId);
			showSuccess("Payment method removed");
			await loadPaymentMethods();
		} catch (error) {
			console.error("Error removing payment method:", error);
			showError("Failed to remove payment method");
		} finally {
			setActionLoading(null);
		}
	};

	return (
		<PaymentMethodListView
			paymentMethods={paymentMethods}
			loading={loading}
			actionLoading={actionLoading}
			onAddNew={onAddNew}
			onSetDefault={handleSetDefault}
			onRemove={handleDelete}
		/>
	);
};

export default PaymentMethodList;
